import React, { useContext, useEffect } from 'react'
import { PeFilterContext } from '../../Contexts/PeFilterContext'
import moment from 'moment'
import { useState } from 'react'
import { toast } from 'react-toastify'
import Popup from '../Common/Popup'


/**
 * The function `getYears` calculates the range of years between a start date and an end date.
 * @param startDate - The `startDate` parameter is the starting date of the range. It should be a valid
 * date string or a `moment` object representing the starting date.
 * @param endDate - The `endDate` parameter represents the end date of the range you want to generate.
 * @returns The function `getYears` returns an array of moment objects representing each year between
 * the `startDate` and `endDate` (inclusive).
 */
const getYears = (startDate, endDate) => {
    let fromDate = moment(startDate)
    let toDate = moment(endDate)
    let diff = toDate.diff(fromDate, 'year')
    let range = []
    for (let i = 0; i <= diff; i++) {
        range.push(moment(startDate).add(i, 'year'))
    }
    return range
}

const monthList = moment.monthsShort().map(month => ({ id: month, name: month, isChecked: false }))
const yearsList = getYears(moment().subtract(25, 'years'), moment()).map(date => ({ id: date.year(), name: date.year(), isChecked: false }));

/**
 * The function `isFutureDate` checks if a given date is in the future.
 * @param date - The `date` parameter is a variable that represents a specific date.
 * @returns a boolean value. It returns true if the provided date is in the future (after the current
 * month), and it returns false if the provided date is in the past or within the current month.
 */
const isFutureDate = (date) => {
    return date?.isAfter(moment(),'month') ?? true
}


/**
 * The `DateFoundedFilter` function is a React component that renders a date filter UI for selecting a
 * range of dates using Popup style.
 * @returns a JSX element, which represents a form for selecting a date range.
 */
function DateFoundedFilter({ setFromDateOption, setToDateOption, isPopupOpen, setIsPopupOpen }) {

    const [FromDate, setFromDate] = useState(moment().subtract(3, 'years'))
    const [ToDate, setToDate] = useState(moment())
    const { monthPickerValue } = useContext(PeFilterContext);
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    
    // Use centralized state if provided, otherwise fall back to internal state
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;

    useEffect(() => {
        console.log('DateFoundedFilter', monthPickerValue);
        setFromDate(moment(monthPickerValue[0]))
        setToDate(moment(monthPickerValue[1]))

        setFromDateOption(moment(monthPickerValue[0]))
        setToDateOption(moment(monthPickerValue[1]))
    }, [monthPickerValue])




 /**
  * The `handleMonth` function updates the selected month for either the "from" or "to" date based on
  * the user's input.
  * @param e - The parameter `e` is an event object that is passed to the `handleMonth` function. It is
  * typically triggered by an event, such as a user selecting a month from a dropdown menu.
  * @returns The function does not have a return statement, so it does not explicitly return anything.
  */
    const handleMonth = (e) => {
        const { name, value } = e.target;

        const selectedMonth = moment().month(value).format('M')

        if (name === "from") {
            const temp = moment(FromDate);
            temp.set('month', parseInt(selectedMonth) - 1)

            if (isFutureDate(temp)) {
                toast.warning('From date cannot be greater than current date')
                return
            }

            if (temp.isAfter(ToDate)) {
                toast.warning('From date cannot be greater than to date')
                return;
            }
            setFromDate(temp)
            setFromDateOption(temp)
        } else {
            const temp = moment(ToDate);
            temp.set('month', parseInt(selectedMonth) - 1)
            if (isFutureDate(temp)) {
                toast.warning('To date cannot be greater than current date')
                return
            }
            if (temp.isBefore(FromDate)) {
                toast.warning('To date cannot be less than from date')
                return;
            }
            setToDate(temp)
            setToDateOption(temp)
        }
    }

 /**
  * The `handleYear` function updates the year value of either the "from" or "to" date based on the
  * user's input, and performs validation checks to ensure the selected dates are within the desired
  * range.
  * @param e - The parameter `e` is an event object that is passed to the `handleYear` function. It is
  * typically triggered by an event, such as a change in the input field value.
  * @returns The function `handleYear` does not have a return statement. Therefore, it does not
  * explicitly return anything.
  */
    const handleYear = (e) => {
        const { name, value } = e.target;

        if (name === "from") {
            const temp = moment(FromDate);
            temp.set('year', parseInt(value))

            if (isFutureDate(temp)) {
                toast.warning('From date cannot be greater than current date')
                return
            }

            if (temp.isAfter(ToDate)) {
                toast.warning('From date cannot be greater than to date')
                return;
            }

            setFromDate(temp)
            setFromDateOption(temp)
        } else {
            const temp = moment(ToDate);
            temp.set('year', parseInt(value))
            if (isFutureDate(temp)) {
                toast.warning('To date cannot be greater than current date')
                return
            }
            if (temp.isBefore(FromDate)) {
                toast.warning('To date cannot be less than from date')
                return;
            }
            setToDate(temp)
            setToDateOption(temp)
        }
    }

    console.log('FromDate', FromDate?.year(), yearsList);

    // Format date range for display
    const getDateRangeDisplay = () => {
        return `${FromDate?.format('MMM YYYY')} - ${ToDate?.format('MMM YYYY')}`;
    };

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-lg px-4 py-1 text-gray-500 hover:bg-gray-100 text-sm shadow-md border border-gray-300 w-full md:w-[90%] text-left flex items-center justify-between"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">Dates Between</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                        {getDateRangeDisplay()}
                    </span>
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)}>
                    <div className="p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Dates Between
                        </label>
                        <div className='flex gap-4 items-center'>
                            <div className='flex flex-col space-y-2'>
                                <label className="text-xs text-gray-600">From</label>
                                <select value={FromDate?.format('MMM')} name="from" onChange={handleMonth}
                                    className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#BC8B20] w-20 md:w-24">
                                    {
                                        monthList?.map(month => (
                                            <option key={month.id} value={month.id}>{month.name}</option>
                                        ))
                                    }
                                </select>
                                <select value={FromDate?.year()} name="from" onChange={handleYear}
                                    className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#BC8B20] w-20 md:w-24">
                                    {
                                        yearsList?.map(month => (
                                            <option key={month.id} value={month.id}>{month.name}</option>
                                        ))
                                    }
                                </select>
                            </div>

                            <span className="text-sm text-gray-600 pt-5">to</span>

                            <div className='flex flex-col space-y-2'>
                                <label className="text-xs text-gray-600">To</label>
                                <select value={ToDate?.format('MMM')} name="to" onChange={handleMonth}
                                    className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#BC8B20] w-20 md:w-24">
                                    {
                                        monthList?.map(month => (
                                            <option key={month.id} value={month.id}>{month.name}</option>
                                        ))
                                    }
                                </select>
                                <select value={ToDate?.year()} name="to" onChange={handleYear}
                                    className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#BC8B20] w-20 md:w-24">
                                    {
                                        yearsList?.map(month => (
                                            <option key={month.id} value={month.id}>{month.name}</option>
                                        ))
                                    }
                                </select>
                            </div>
                        </div>
                    </div>
                </Popup>
            )}
        </div>
    )
}

export default DateFoundedFilter